import { elements } from '../../../dom';
import { slugify } from '../../../utils/format';
import {
  createDraftIdeaExampleIndexCycle,
  createDraftIdeaPlaceholderCycle,
  draftIdeaExampleLabels,
  draftIdeaPlaceholders,
} from '../idea-placeholders';
import { countSpokenWords, getScriptDurationTarget } from '../script-length';
import type { DraftAnswers, DraftChoice } from '../types.ts';

export type DraftEntryPath = 'recommendations' | 'customize';

type DraftScriptControlsOptions = {
  onFindChoices: (idea: string, path: DraftEntryPath) => void;
  onGenerateScript: (answers: DraftAnswers) => void;
  onScriptInput: (script: string) => void;
  onReset: () => void;
  onRetryOptions: () => void;
  onRetryScript: () => void;
};

export function createDraftScriptControls({
  onFindChoices,
  onGenerateScript,
  onScriptInput,
  onReset,
  onRetryOptions,
  onRetryScript,
}: DraftScriptControlsOptions) {
  let choices: DraftChoice[] = [];
  let wizardIndex = 0;
  let showCustomization = true;
  let isEditingGeneratedScript = false;
  let progressPhase: DraftStep = 'idea';
  let openProgressKey: string | null = 'idea';
  let downloadUrl: string | null = null;
  let copyResetTimer = 0;
  let writingProgressTimer = 0;
  let writingProgressIndex = 0;
  let scriptRevealTimer = 0;
  let isScriptRevealPending = false;
  let draftIdeaPlaceholderTransitionTimer = 0;
  let draftExampleTransitionTimers: number[] = [];
  let hasSetDraftIdeaPlaceholder = false;
  let haveSetDraftExamples = false;
  const nextIdeaPlaceholder = createDraftIdeaPlaceholderCycle();
  const nextExampleIndex = createDraftIdeaExampleIndexCycle();
  const writingProgressMessages = [
    'Finding the sharpest angle for your story.',
    'Building the structure and pacing.',
    'Writing the opening hook.',
    'Giving the ending something to land on.',
  ];

  setNextIdeaPlaceholder();
  rotateExampleButtons();
  renderProgress();
  setProgress('idea');
  window.setInterval(() => {
    if (!elements.draftIdeaInput.value.trim() && document.activeElement !== elements.draftIdeaInput) {
      setNextIdeaPlaceholder();
    }
  }, 6000);
  window.setInterval(() => {
    if (!elements.draftIdeaInput.value.trim() && !isExampleButtonFocused()) {
      rotateExampleButtons();
    }
  }, 8000);

  elements.draftIdeaForm.addEventListener('submit', (event) => {
    event.preventDefault();
    submitIdea('recommendations');
  });
  elements.draftCustomizeButton.addEventListener('click', () => submitIdea('customize'));
  elements.draftIdeaInput.addEventListener('input', () => {
    cancelDraftIdeaPlaceholderTransition();
    clearIdeaValidation();
    updateProgressAccordion();
  });
  elements.draftIdeaInput.addEventListener('focus', cancelDraftIdeaPlaceholderTransition);
  elements.draftIdeaExampleButtons.forEach((button) => {
    button.addEventListener('click', () => {
      elements.draftIdeaInput.value = button.dataset.draftIdea ?? '';
      clearIdeaValidation();
      updateProgressAccordion();
      submitIdea('recommendations');
    });
  });

  elements.draftOptionsForm.addEventListener('submit', (event) => {
    event.preventDefault();
    const currentChoice = choices[wizardIndex];
    const answers = readAnswers();
    if (currentChoice?.required && isEmptyAnswer(answers[currentChoice.id])) {
      setChoiceValidation(`Choose an option for “${currentChoice.label}” to continue.`);
      return;
    }
    clearChoiceValidation();

    if (!isEditingGeneratedScript && wizardIndex < choices.length - 1) {
      setWizardIndex(wizardIndex + 1);
      return;
    }

    onGenerateScript(answers);
  });

  elements.draftScriptInput.addEventListener('input', () => {
    onScriptInput(elements.draftScriptInput.value);
    updateScriptSummary(elements.draftScriptInput.value);
    updateDownloadLink();
  });

  elements.draftScriptCopyButton.addEventListener('click', () => void copyScript());
  elements.draftEditSettingsButton.addEventListener('click', editSettings);
  elements.draftRegenerateButton.addEventListener('click', () => onGenerateScript(readAnswers()));
  elements.draftWizardBackButton.addEventListener('click', handleWizardBack);
  elements.draftResetButton.addEventListener('click', onReset);
  elements.draftIdeaRetryButton.addEventListener('click', onRetryOptions);
  elements.draftScriptRetryButton.addEventListener('click', onRetryScript);

  return {
    setIdea(idea: string) {
      elements.draftIdeaInput.value = idea;
    },
    setOptions(nextChoices: DraftChoice[], shouldShowCustomization = true) {
      cancelPendingScriptReveal();
      choices = nextChoices;
      wizardIndex = 0;
      showCustomization = shouldShowCustomization;
      isEditingGeneratedScript = false;
      renderProgress();
      renderChoices();
      setProgress('customize');
      elements.draftIdeaForm.hidden = true;
      elements.draftOptionsForm.hidden = !showCustomization;
      elements.draftScriptPanel.hidden = true;
      elements.draftEmptyOutput.hidden = false;
      elements.draftWritingOutput.hidden = true;
      elements.draftScriptRefreshing.hidden = true;
      hideRecoveries();
      clearInlineValidations();
    },
    getAnswers() {
      return readAnswers();
    },
    setOptionsLoading(isLoading: boolean, path: DraftEntryPath = 'customize') {
      if (isLoading) {
        setProgress('idea');
        elements.draftIdeaRecovery.hidden = true;
        setOptionsStatus('Checking your topic…');
        elements.draftWritingTitle.textContent = path === 'recommendations'
          ? 'Preparing your script'
          : 'Preparing your settings';
        elements.draftWritingProgress.textContent = path === 'recommendations'
          ? 'Turning your idea into a strong starting point.'
          : 'Finding the best choices for your topic.';
        elements.draftWritingOutput.hidden = false;
        elements.draftEmptyOutput.hidden = true;
      } else {
        setOptionsStatus();
        elements.draftWritingOutput.hidden = true;
        if (elements.draftScriptPanel.hidden && elements.draftScriptRecovery.hidden) {
          elements.draftEmptyOutput.hidden = false;
        }
      }
      elements.draftIdeaInput.disabled = isLoading;
      elements.draftUseRecommendationsButton.disabled = isLoading;
      elements.draftCustomizeButton.disabled = isLoading;
      elements.draftUseRecommendationsButton.classList.toggle('busy', isLoading && path === 'recommendations');
      elements.draftCustomizeButton.classList.toggle('busy', isLoading && path === 'customize');
      elements.draftUseRecommendationsButton.querySelector('span')!.textContent = isLoading && path === 'recommendations'
        ? 'Finding recommendations…'
        : 'Draft with suggested settings';
      elements.draftCustomizeButton.querySelector('span')!.textContent = isLoading && path === 'customize'
        ? 'Preparing settings…'
        : 'Choose tone, format & length';
      elements.draftOptionsForm.hidden = isLoading || choices.length === 0 || !showCustomization;
    },
    setOptionsRetryStatus(nextAttempt: number, totalAttempts: number) {
      const message = nextAttempt === totalAttempts
        ? `We didn’t get settings yet — one more try (${nextAttempt} of ${totalAttempts})…`
        : `We didn’t get settings yet — trying again (${nextAttempt} of ${totalAttempts})…`;
      setOptionsStatus(message);
    },
    setAnswering(isDisabled: boolean) {
      setProgress(isDisabled ? 'review' : 'customize');
      setFormDisabled(elements.draftOptionsForm, isDisabled);
      setProgressInputsDisabled(isDisabled);
      elements.draftGenerateButton.disabled = isDisabled;
      elements.draftGenerateButton.classList.toggle('busy', isDisabled);
      elements.draftGenerateButton.querySelector('span')!.textContent = isDisabled
        ? 'Generating script…'
        : getGenerateButtonLabel();
    },
    setScript(title: string, script: string) {
      setProgress('review');
      const shouldRevealFromLoading = !elements.draftWritingOutput.hidden;
      const shouldReplaceExistingScript = elements.draftScriptPanel.classList.contains('is-refreshing') && hasGeneratedScript();
      cancelPendingScriptReveal();
      isEditingGeneratedScript = false;
      elements.draftOptionsForm.hidden = true;
      elements.draftEmptyOutput.hidden = true;
      stopWritingProgress();
      elements.draftScriptRefreshing.hidden = true;
      elements.draftScriptRecovery.hidden = true;
      clearInlineValidations();
      if (shouldReplaceExistingScript) {
        revealReplacementScript(title, script);
        return;
      }

      applyScriptResult(title, script);
      elements.draftScriptPanel.classList.remove('is-refreshing');
      elements.draftScriptInput.readOnly = false;
      revealScriptResult(shouldRevealFromLoading);
    },
    setScriptLoading(isLoading: boolean) {
      if (isLoading) {
        cancelPendingScriptReveal();
        setProgress('review');
        const writingTitle = getWritingTitle();
        elements.draftWritingTitle.textContent = writingTitle;
        const isReplacingExistingScript = !elements.draftScriptPanel.hidden && Boolean(elements.draftScriptInput.value.trim());
        if (isReplacingExistingScript) {
          stopWritingProgress();
        } else {
          startWritingProgress();
        }
        elements.draftScriptPanel.classList.toggle('is-refreshing', isReplacingExistingScript);
        elements.draftScriptInput.readOnly = isReplacingExistingScript;
        elements.draftScriptRefreshing.hidden = !isReplacingExistingScript;
        elements.draftScriptPanel.hidden = false;
        elements.draftEmptyOutput.hidden = isReplacingExistingScript;
        elements.draftWritingOutput.hidden = isReplacingExistingScript;
        if (!isReplacingExistingScript) {
          elements.draftScriptPanel.hidden = true;
          elements.draftEmptyOutput.hidden = true;
          elements.draftWritingOutput.hidden = false;
        }
        elements.draftScriptRecovery.hidden = true;
      } else {
        if (!isScriptRevealPending) {
          elements.draftWritingOutput.hidden = true;
        }
        stopWritingProgress();
        elements.draftScriptRefreshing.hidden = true;
        elements.draftScriptPanel.classList.remove('is-refreshing');
        elements.draftScriptInput.readOnly = false;
        if (elements.draftScriptPanel.hidden && elements.draftScriptRecovery.hidden && !isScriptRevealPending) {
          elements.draftEmptyOutput.hidden = false;
        }
      }
      setFormDisabled(elements.draftOptionsForm, isLoading);
      setProgressInputsDisabled(isLoading);
      elements.draftGenerateButton.disabled = isLoading;
      elements.draftGenerateButton.classList.toggle('busy', isLoading);
      elements.draftGenerateButton.querySelector('span')!.textContent = isLoading ? 'Generating script…' : getGenerateButtonLabel();
      setRegenerationActionsLoading(isLoading);
    },
    setOptionsError(message: string) {
      setOptionsStatus();
      elements.draftWritingOutput.hidden = true;
      elements.draftEmptyOutput.hidden = true;
      elements.draftIdeaErrorMessage.textContent = message;
      elements.draftIdeaRecovery.hidden = false;
    },
    setScriptError(message: string, retryable = true) {
      cancelPendingScriptReveal();
      elements.draftScriptErrorMessage.textContent = message;
      elements.draftScriptRetryButton.hidden = !retryable;
      elements.draftWritingOutput.hidden = true;
      stopWritingProgress();
      elements.draftEmptyOutput.hidden = true;
      elements.draftScriptRecovery.hidden = false;
    },
    setReady() {
      clearInlineValidations();
    },
    reset() {
      cancelPendingScriptReveal();
      choices = [];
      wizardIndex = 0;
      showCustomization = true;
      isEditingGeneratedScript = false;
      elements.draftOptionsForm.reset();
      elements.draftIdeaForm.hidden = false;
      elements.draftOptionsForm.hidden = true;
      elements.draftScriptPanel.hidden = true;
      elements.draftEmptyOutput.hidden = false;
      elements.draftWritingOutput.hidden = true;
      stopWritingProgress();
      elements.draftScriptRefreshing.hidden = true;
      hideRecoveries();
      elements.draftScriptInput.value = '';
      elements.draftScriptInput.readOnly = false;
      elements.draftScriptTitle.textContent = '';
      elements.draftIdeaInput.disabled = false;
      setNextIdeaPlaceholder();
      elements.draftUseRecommendationsButton.disabled = false;
      elements.draftCustomizeButton.disabled = false;
      elements.draftUseRecommendationsButton.classList.remove('busy');
      elements.draftCustomizeButton.classList.remove('busy');
      elements.draftUseRecommendationsButton.querySelector('span')!.textContent = 'Draft with suggested settings';
      elements.draftCustomizeButton.querySelector('span')!.textContent = 'Choose tone, format & length';
      setFormDisabled(elements.draftOptionsForm, false);
      setProgressInputsDisabled(false);
      elements.draftGenerateButton.disabled = false;
      elements.draftGenerateButton.classList.remove('busy');
      elements.draftGenerateButton.querySelector('span')!.textContent = 'Next';
      setRegenerationActionsLoading(false);
      setWizardBackLabel('Edit topic');
      elements.draftWizardBackButton.hidden = false;
      revokeDownloadUrl();
      clearInlineValidations();
      setOptionsStatus();
      renderProgress();
      setProgress('idea');
    },
  };

  function submitIdea(path: DraftEntryPath) {
    const idea = elements.draftIdeaInput.value.trim();
    if (!idea) {
      setIdeaValidation('Add a podcast topic to continue.');
      elements.draftIdeaInput.focus();
      return;
    }
    onFindChoices(idea, path);
  }

  function renderChoices() {
    renderProgress();
    const choiceSteps = Array.from(elements.draftProgress.querySelectorAll<HTMLElement>('[data-draft-step="choice"]'));
    choices.forEach((choice, index) => {
      const panel = choiceSteps[index]?.querySelector<HTMLElement>('.draft-progress-panel');
      panel?.replaceChildren(renderChoice(choice));
    });
    setWizardIndex(0);
  }

  function renderChoice(choice: DraftChoice) {
    const field = document.createElement('fieldset');
    field.className = 'draft-choice-field';

    const legend = document.createElement('legend');
    legend.textContent = `${choice.label}${choice.required ? ' *' : ' (optional)'}`;
    field.append(legend, createInput(choice));

    if (choice.helpText) {
      const help = document.createElement('small');
      help.className = 'draft-choice-help';
      help.id = `draft-help-${choice.id}`;
      help.textContent = choice.helpText;
      field.setAttribute('aria-describedby', help.id);
      field.append(help);
    }
    return field;
  }

  function createInput(choice: DraftChoice) {
    const options = document.createElement('div');
    options.className = `draft-option-grid${choice.type === 'multiselect' ? ' is-multiselect' : ''}`;
    let selectedRecommendedOption = false;
    for (const [index, option] of choice.options.entries()) {
      const optionLabel = document.createElement('label');
      optionLabel.className = 'draft-option-card';

      const input = document.createElement('input');
      input.type = choice.type === 'multiselect' ? 'checkbox' : 'radio';
      input.name = choice.id;
      input.value = option.value;
      input.dataset.draftChoiceId = choice.id;
      input.id = `draft-option-${choice.id}-${slugify(option.value)}`;

      const isRecommended = /\brecommended\b/i.test(option.label);
      if (isRecommended && (choice.type === 'multiselect' || !selectedRecommendedOption)) {
        input.checked = true;
        selectedRecommendedOption = true;
      }

      if (!isRecommended && !selectedRecommendedOption && choice.type === 'select' && index === 0) {
        input.checked = true;
      }

      const marker = document.createElement('span');
      marker.className = 'draft-option-marker';
      marker.setAttribute('aria-hidden', 'true');

      const content = document.createElement('span');
      content.className = 'draft-option-content';
      const label = document.createElement('span');
      label.className = 'draft-option-label';
      label.textContent = option.label.replace(/\s*\(recommended\)\s*/i, '').trim();
      content.append(label);
      if (isRecommended) {
        const badge = document.createElement('small');
        badge.className = 'draft-option-badge';
        badge.textContent = 'Recommended';
        content.append(badge);
      }

      optionLabel.append(input, marker, content);
      options.append(optionLabel);
      input.addEventListener('change', () => {
        clearChoiceValidation();
        updateProgressAccordion();
        if (!elements.draftScriptPanel.hidden) {
          updateScriptSummary(elements.draftScriptInput.value);
        }
      });
    }
    return options;
  }

  function readAnswers(): DraftAnswers {
    const answers: DraftAnswers = {};
    for (const choice of choices) {
      const inputs = Array.from(
        elements.draftProgress.querySelectorAll<HTMLInputElement>(`[data-draft-choice-id="${choice.id}"]`),
      );
      if (inputs.length === 0) continue;
      const value = choice.type === 'multiselect'
        ? inputs.filter((input) => input.checked).map((input) => input.value).filter(Boolean)
        : inputs.find((input) => input.checked)?.value.trim() ?? '';
      answers[choice.id] = value;
    }
    return answers;
  }

  function renderProgress() {
    const steps: HTMLElement[] = [
      createProgressStep('idea', 'Idea', 'Describe the podcast topic', 1),
    ];

    if (choices.length === 0) {
      steps.push(createLockedProgressPreview('Next: choose settings', 'Available after you choose a draft path.', 2));
    } else {
      choices.forEach((choice, index) => {
        steps.push(createProgressStep('choice', choice.label, choice.required ? 'Required choice' : 'Optional choice', index + 2, index));
      });
    }

    elements.draftProgress.replaceChildren(...steps);
    const ideaPanel = elements.draftProgress.querySelector<HTMLElement>('[data-draft-step="idea"] .draft-progress-panel');
    ideaPanel?.replaceChildren(elements.draftIdeaForm);
    openProgressKey = progressKeyForPhase();
    updateProgressAccordion();
  }

  function createProgressStep(kind: ProgressStepKind, label: string, panelCopy: string, stepNumber: number, choiceIndex?: number) {
    const key = choiceIndex === undefined ? kind : `choice:${choiceIndex}`;
    const step = document.createElement('li');
    step.className = 'draft-progress-step';
    step.dataset.draftStep = kind;
    if (choiceIndex !== undefined) {
      step.dataset.choiceIndex = String(choiceIndex);
    }

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'draft-progress-trigger';
    trigger.setAttribute('aria-expanded', 'false');

    const panelId = `draft-progress-panel-${kind}-${choiceIndex ?? stepNumber}`;
    trigger.setAttribute('aria-controls', panelId);

    const marker = document.createElement('span');
    marker.className = 'draft-progress-marker';
    marker.textContent = String(stepNumber);

    const copy = document.createElement('span');
    copy.className = 'draft-progress-copy';
    const title = document.createElement('strong');
    title.textContent = label;
    const summary = document.createElement('small');
    summary.dataset.progressSummary = 'true';
    summary.textContent = panelCopy;
    copy.append(title, summary);

    const chevron = document.createElement('span');
    chevron.className = 'draft-progress-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '⌄';

    trigger.append(marker, copy, chevron);

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'draft-progress-panel';
    panel.hidden = true;
    const panelText = document.createElement('span');
    panelText.dataset.progressPanelCopy = 'true';
    panelText.textContent = panelCopy;
    panel.append(panelText);

    trigger.addEventListener('click', () => handleProgressClick(kind, choiceIndex, key));
    step.append(trigger, panel);
    return step;
  }

  function createLockedProgressPreview(label: string, summaryCopy: string, stepNumber: number) {
    const step = document.createElement('li');
    step.className = 'draft-progress-step is-locked';

    const preview = document.createElement('div');
    preview.className = 'draft-progress-preview';
    preview.setAttribute('aria-label', `Step ${stepNumber}: ${label}. Locked. ${summaryCopy}`);

    const marker = document.createElement('span');
    marker.className = 'draft-progress-marker';
    marker.textContent = String(stepNumber);

    const copy = document.createElement('span');
    copy.className = 'draft-progress-copy';
    const title = document.createElement('strong');
    title.textContent = label;
    const summary = document.createElement('small');
    summary.textContent = summaryCopy;
    copy.append(title, summary);

    const lock = document.createElement('span');
    lock.className = 'draft-progress-lock';
    lock.textContent = 'Locked';

    preview.append(marker, copy, lock);
    step.append(preview);
    return step;
  }

  function handleProgressClick(kind: ProgressStepKind, choiceIndex: number | undefined, key: string) {
    if (openProgressKey === key) {
      openProgressKey = null;
      updateProgressAccordion();
      return;
    }

    if (kind === 'idea') {
      showIdeaStep();
      return;
    }

    if (kind === 'choice' && choiceIndex !== undefined) {
      isEditingGeneratedScript = hasGeneratedScript();
      elements.draftIdeaForm.hidden = true;
      elements.draftOptionsForm.hidden = false;
      setWizardIndex(choiceIndex);
      setProgress('customize');
      openProgressKey = key;
      updateProgressAccordion();
      return;
    }

    openProgressKey = key;
    updateProgressAccordion();
  }

  function setProgress(currentStep: DraftStep) {
    progressPhase = currentStep;
    openProgressKey = progressKeyForPhase();
    updateProgressAccordion();
  }

  function progressKeyForPhase() {
    if (progressPhase === 'review') return null;
    return progressPhase === 'customize' && choices.length > 0 ? `choice:${wizardIndex}` : progressPhase;
  }

  function updateProgressAccordion() {
    const answers = readAnswers();
    const steps = Array.from(elements.draftProgress.querySelectorAll<HTMLElement>('.draft-progress-step'));
    steps.forEach((step) => {
      if (step.classList.contains('is-locked')) return;

      const kind = step.dataset.draftStep as ProgressStepKind;
      const choiceIndex = step.dataset.choiceIndex === undefined ? undefined : Number(step.dataset.choiceIndex);
      const key = choiceIndex === undefined ? kind : `choice:${choiceIndex}`;
      const isOpen = openProgressKey === key;
      const isActive = kind === 'idea'
        ? progressPhase === 'idea'
        : kind === 'customize'
          ? progressPhase === 'customize' && choices.length === 0
          : progressPhase === 'customize' && choiceIndex === wizardIndex;
      const isComplete = kind === 'idea'
        ? progressPhase !== 'idea'
        : kind === 'choice' && choiceIndex !== undefined
          ? progressPhase === 'review' || progressPhase === 'idea' || (progressPhase === 'customize' && choiceIndex < wizardIndex)
          : false;

      step.classList.toggle('is-open', isOpen);
      step.classList.toggle('is-active', isActive);
      step.classList.toggle('is-complete', isComplete);
      if (isActive) {
        step.setAttribute('aria-current', 'step');
      } else {
        step.removeAttribute('aria-current');
      }

      const trigger = step.querySelector<HTMLButtonElement>('.draft-progress-trigger');
      const panel = step.querySelector<HTMLElement>('.draft-progress-panel');
      trigger?.setAttribute('aria-expanded', String(isOpen));
      if (panel) panel.hidden = !isOpen;

      const summary = step.querySelector<HTMLElement>('[data-progress-summary]');
      const panelCopy = step.querySelector<HTMLElement>('[data-progress-panel-copy]');
      if (kind === 'idea') {
        const idea = elements.draftIdeaInput.value.trim();
        if (summary) summary.textContent = idea || 'Describe the podcast topic';
        if (panelCopy) panelCopy.textContent = idea ? 'Topic added — select to edit it.' : 'Start with a podcast topic.';
      } else if (kind === 'choice' && choiceIndex !== undefined) {
        const choice = choices[choiceIndex];
        const answer = choice ? answers[choice.id] : undefined;
        if (summary) summary.textContent = choice ? summarizeChoice(choice, answer) : 'Choose an option';
        if (panelCopy) panelCopy.textContent = isComplete
          ? 'Complete — select to edit this choice.'
          : isActive ? 'Edit this choice below.' : 'Select this step to edit it.';
      } else if (kind === 'customize') {
        if (summary) summary.textContent = 'Choose the angle';
        if (panelCopy) panelCopy.textContent = 'Your generated choices will appear here.';
      }
    });
  }

  function summarizeChoice(choice: DraftChoice, answer: string | string[] | undefined) {
    if (isEmptyAnswer(answer)) {
      return choice.required ? 'Choose an option' : 'Optional — not selected';
    }

    const values = Array.isArray(answer) ? answer : [answer];
    const labels = values.map((value) => choice.options.find((option) => option.value === value)?.label.replace(/\s*\(recommended\)\s*/i, '').trim()).filter(Boolean);
    return labels.join(', ') || 'Selected';
  }

  function setWizardIndex(nextIndex: number) {
    if (choices.length === 0) return;
    wizardIndex = Math.max(0, Math.min(nextIndex, choices.length - 1));
    const fields = Array.from(elements.draftProgress.querySelectorAll<HTMLElement>('.draft-choice-field'));
    fields.forEach((field, index) => {
      field.hidden = index !== wizardIndex;
    });

    setWizardBackLabel(wizardIndex === 0 ? 'Edit topic' : 'Back');
    const isGenerateAction = isEditingGeneratedScript || wizardIndex === choices.length - 1;
    elements.draftGenerateButton.classList.toggle('is-final-step', isGenerateAction);
    elements.draftGenerateButton.querySelector('span')!.textContent = isGenerateAction ? 'Generate script' : 'Next';
    if (progressPhase === 'customize') {
      openProgressKey = `choice:${wizardIndex}`;
    }
    updateProgressAccordion();
  }

  function handleWizardBack() {
    if (wizardIndex > 0) {
      clearChoiceValidation();
      setWizardIndex(wizardIndex - 1);
      return;
    }

    showIdeaStep();
  }

  function editSettings() {
    if (choices.length === 0) return;
    showCustomization = true;
    isEditingGeneratedScript = true;
    elements.draftIdeaForm.hidden = true;
    elements.draftOptionsForm.hidden = false;
    setProgress('customize');
    setWizardIndex(0);
    elements.draftProgress.querySelector<HTMLButtonElement>('[data-draft-step="choice"] .draft-progress-trigger')?.focus();
  }

  function hasGeneratedScript() {
    return !elements.draftScriptPanel.hidden && Boolean(elements.draftScriptInput.value.trim());
  }

  function getGenerateButtonLabel() {
    return isEditingGeneratedScript || wizardIndex === choices.length - 1 ? 'Generate script' : 'Next';
  }

  function setWizardBackLabel(label: string) {
    elements.draftWizardBackButton.setAttribute('aria-label', label);
    elements.draftWizardBackButton.title = label;
  }

  function setRegenerationActionsLoading(isLoading: boolean) {
    elements.draftRegenerateButton.disabled = isLoading;
    elements.draftRegenerateButton.classList.toggle('busy', isLoading);
    elements.draftRegenerateButton.querySelector('span')!.textContent = isLoading ? 'Regenerating…' : 'Regenerate';
  }

  function revealScriptResult(shouldTransition: boolean) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!shouldTransition || reducedMotion) {
      showScriptResult(false);
      return;
    }

    isScriptRevealPending = true;
    elements.draftWritingOutput.classList.add('is-resolving');
    window.clearTimeout(scriptRevealTimer);
    scriptRevealTimer = window.setTimeout(() => {
      isScriptRevealPending = false;
      elements.draftWritingOutput.classList.remove('is-resolving');
      elements.draftWritingOutput.hidden = true;
      showScriptResult(true);
    }, 180);
  }

  function revealReplacementScript(title: string, script: string) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      applyScriptResult(title, script);
      elements.draftScriptPanel.classList.remove('is-refreshing');
      elements.draftScriptInput.readOnly = false;
      showScriptResult(false);
      return;
    }

    isScriptRevealPending = true;
    elements.draftScriptPanel.classList.add('is-resolving');
    window.clearTimeout(scriptRevealTimer);
    scriptRevealTimer = window.setTimeout(() => {
      isScriptRevealPending = false;
      elements.draftScriptPanel.classList.remove('is-resolving', 'is-refreshing');
      applyScriptResult(title, script);
      elements.draftScriptInput.readOnly = false;
      showScriptResult(true);
    }, 180);
  }

  function applyScriptResult(title: string, script: string) {
    elements.draftScriptTitle.textContent = title;
    elements.draftScriptInput.value = script;
    updateScriptSummary(script);
    updateDownloadLink();
  }

  function showScriptResult(animate: boolean) {
    elements.draftWritingOutput.hidden = true;
    elements.draftScriptPanel.hidden = false;
    elements.draftScriptPanel.classList.remove('is-revealing');
    if (animate) {
      void elements.draftScriptPanel.offsetWidth;
      elements.draftScriptPanel.classList.add('is-revealing');
    }
    elements.draftScriptInput.setSelectionRange(0, 0);
    elements.draftScriptInput.focus();
    elements.draftScriptInput.scrollTop = 0;
    elements.draftScriptInput.scrollLeft = 0;
  }

  function cancelPendingScriptReveal() {
    window.clearTimeout(scriptRevealTimer);
    scriptRevealTimer = 0;
    isScriptRevealPending = false;
    elements.draftWritingOutput.classList.remove('is-resolving');
    elements.draftScriptPanel.classList.remove('is-resolving');
  }

  function startWritingProgress() {
    window.clearInterval(writingProgressTimer);
    writingProgressIndex = 0;
    setWritingProgressMessage();
    writingProgressTimer = window.setInterval(() => {
      writingProgressIndex = (writingProgressIndex + 1) % writingProgressMessages.length;
      setWritingProgressMessage();
    }, 2400);
  }

  function stopWritingProgress() {
    window.clearInterval(writingProgressTimer);
    writingProgressTimer = 0;
    writingProgressIndex = 0;
    elements.draftWritingProgress.classList.remove('is-changing');
    elements.draftWritingProgress.textContent = writingProgressMessages[writingProgressIndex];
  }

  function setWritingProgressMessage() {
    elements.draftWritingProgress.classList.remove('is-changing');
    elements.draftWritingProgress.textContent = writingProgressMessages[writingProgressIndex];
    void elements.draftWritingProgress.offsetWidth;
    elements.draftWritingProgress.classList.add('is-changing');
  }

  function showIdeaStep() {
    isEditingGeneratedScript = false;
    elements.draftOptionsForm.hidden = choices.length === 0;
    elements.draftIdeaForm.hidden = false;
    setProgress('idea');
    elements.draftIdeaInput.focus();
  }

  function updateDownloadLink() {
    revokeDownloadUrl();
    const script = elements.draftScriptInput.value;
    if (!script.trim()) {
      elements.draftDownloadLink.hidden = true;
      return;
    }
    downloadUrl = URL.createObjectURL(new Blob([script], { type: 'text/plain;charset=utf-8' }));
    elements.draftDownloadLink.href = downloadUrl;
    elements.draftDownloadLink.download = `${slugify(elements.draftScriptTitle.textContent || 'draft-script')}.txt`;
    elements.draftDownloadLink.classList.add('is-ready');
    elements.draftDownloadLink.hidden = false;
  }

  async function copyScript() {
    const script = elements.draftScriptInput.value;
    if (!script) return;
    try {
      await navigator.clipboard.writeText(script);
    } catch {
      elements.draftScriptInput.focus();
      elements.draftScriptInput.select();
      document.execCommand('copy');
    }
    elements.draftScriptCopyButton.setAttribute('aria-label', 'Copied');
    elements.draftScriptCopyButton.title = 'Copied';
    elements.draftScriptCopyButton.querySelector('span')!.textContent = 'Copied';
    window.clearTimeout(copyResetTimer);
    copyResetTimer = window.setTimeout(() => {
      elements.draftScriptCopyButton.setAttribute('aria-label', 'Copy script');
      elements.draftScriptCopyButton.title = 'Copy script';
      elements.draftScriptCopyButton.querySelector('span')!.textContent = 'Copy';
    }, 1400);
  }

  function setNextIdeaPlaceholder() {
    const input = elements.draftIdeaInput;
    const placeholder = nextIdeaPlaceholder();
    cancelDraftIdeaPlaceholderTransition();

    if (!hasSetDraftIdeaPlaceholder || prefersReducedMotion()) {
      input.placeholder = placeholder;
      hasSetDraftIdeaPlaceholder = true;
      return;
    }

    input.classList.add('is-placeholder-fading');
    draftIdeaPlaceholderTransitionTimer = window.setTimeout(() => {
      input.placeholder = placeholder;
      input.classList.remove('is-placeholder-fading');
      void input.offsetWidth;
      input.classList.add('is-placeholder-entering');
      draftIdeaPlaceholderTransitionTimer = window.setTimeout(() => {
        input.classList.remove('is-placeholder-entering');
        draftIdeaPlaceholderTransitionTimer = 0;
      }, 180);
    }, 140);
  }

  function cancelDraftIdeaPlaceholderTransition() {
    window.clearTimeout(draftIdeaPlaceholderTransitionTimer);
    draftIdeaPlaceholderTransitionTimer = 0;
    elements.draftIdeaInput.classList.remove('is-placeholder-fading', 'is-placeholder-entering');
  }

  function getWritingTitle() {
    const target = getScriptDurationTarget(choices, readAnswers());
    if (!target) return 'Writing your script';

    const unit = target.seconds % 60 === 0 ? 'minute' : 'second';
    const amount = target.seconds % 60 === 0 ? target.seconds / 60 : target.seconds;
    return `Writing your ${amount}-${unit} script`;
  }

  function setIdeaValidation(message: string) {
    elements.draftIdeaValidation.textContent = message;
    elements.draftIdeaValidation.hidden = false;
    elements.draftIdeaInput.setAttribute('aria-invalid', 'true');
  }

  function clearIdeaValidation() {
    elements.draftIdeaValidation.hidden = true;
    elements.draftIdeaInput.removeAttribute('aria-invalid');
  }

  function setChoiceValidation(message: string) {
    const panel = elements.draftProgress.querySelector<HTMLElement>(`[data-draft-step="choice"][data-choice-index="${wizardIndex}"] .draft-progress-panel`);
    if (!panel) return;
    let error = panel.querySelector<HTMLElement>('.draft-choice-validation');
    if (!error) {
      error = document.createElement('p');
      error.className = 'draft-inline-error draft-choice-validation';
      error.setAttribute('role', 'alert');
      panel.append(error);
    }
    error.textContent = message;
    error.hidden = false;
  }

  function clearChoiceValidation() {
    elements.draftProgress.querySelectorAll<HTMLElement>('.draft-choice-validation').forEach((error) => {
      error.hidden = true;
    });
  }

  function clearInlineValidations() {
    clearIdeaValidation();
    clearChoiceValidation();
  }

  function updateScriptSummary(script: string) {
    const wordCount = countSpokenWords(script);
    elements.draftWordCount.textContent = wordCount ? `${wordCount.toLocaleString()} words` : '—';
    elements.draftReadTime.textContent = wordCount ? formatEstimatedReadTime(wordCount) : getSelectedDurationLabel();
    elements.draftAngleTone.textContent = getSelectedAngleTone();
  }

  function formatEstimatedReadTime(wordCount: number) {
    const seconds = Math.max(1, Math.round((wordCount / 145) * 60));
    if (seconds < 60) return `~${seconds} sec`;
    const minutes = Math.round(seconds / 60);
    return `~${minutes} min`;
  }

  function getSelectedDurationLabel() {
    const target = getScriptDurationTarget(choices, readAnswers());
    if (!target) return '—';
    return target.seconds % 60 === 0 ? `~${target.seconds / 60} min` : `~${target.seconds} sec`;
  }

  function getSelectedAngleTone() {
    const angle = getSelectedOptionLabel('story_angle');
    const tone = getSelectedOptionLabel('editorial_edge');
    return [angle, tone].filter(Boolean).join(' · ') || '—';
  }

  function getSelectedOptionLabel(choiceId: string) {
    const choice = choices.find((item) => item.id === choiceId);
    const answer = choice ? readAnswers()[choice.id] : undefined;
    const value = Array.isArray(answer) ? answer[0] : answer;
    return choice?.options.find((option) => option.value === value)?.label.replace(/\s*\(recommended\)\s*/i, '').trim() ?? '';
  }

  function hideRecoveries() {
    elements.draftIdeaRecovery.hidden = true;
    elements.draftScriptRecovery.hidden = true;
    elements.draftScriptRetryButton.hidden = false;
  }

  function setOptionsStatus(message = '') {
    elements.draftOptionsStatusMessage.textContent = message;
    elements.draftOptionsStatus.hidden = !message;
  }

  function rotateExampleButtons() {
    const nextExamples = elements.draftIdeaExampleButtons.map((button) => {
      const exampleIndex = nextExampleIndex();
      return exampleIndex === null ? null : { button, exampleIndex };
    });

    cancelDraftExampleTransitions();
    nextExamples.forEach((example, index) => {
      if (!example) return;
      if (!haveSetDraftExamples || prefersReducedMotion()) {
        setDraftExample(example.button, example.exampleIndex);
        return;
      }
      draftExampleTransitionTimers.push(window.setTimeout(() => {
        transitionDraftExample(example.button, example.exampleIndex);
      }, index * 80));
    });
    haveSetDraftExamples = true;
  }

  function cancelDraftExampleTransitions() {
    draftExampleTransitionTimers.forEach((timer) => window.clearTimeout(timer));
    draftExampleTransitionTimers = [];
    elements.draftIdeaExampleButtons.forEach((button) => {
      button.querySelector('.draft-example-label')?.classList.remove('is-fading-out', 'is-fading-in');
    });
  }

  function transitionDraftExample(button: HTMLButtonElement, exampleIndex: number) {
    const label = button.querySelector<HTMLElement>('.draft-example-label');
    if (!label) return;
    label.classList.add('is-fading-out');
    draftExampleTransitionTimers.push(window.setTimeout(() => {
      setDraftExample(button, exampleIndex);
      label.classList.remove('is-fading-out');
      void label.offsetWidth;
      label.classList.add('is-fading-in');
      draftExampleTransitionTimers.push(window.setTimeout(() => label.classList.remove('is-fading-in'), 180));
    }, 140));
  }

  function setDraftExample(button: HTMLButtonElement, exampleIndex: number) {
    const label = draftIdeaExampleLabels[exampleIndex];
    button.dataset.draftIdea = draftIdeaPlaceholders[exampleIndex];
    button.querySelector<HTMLElement>('.draft-example-label')!.textContent = label;
    button.setAttribute('aria-label', `Use the ${label} example`);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isExampleButtonFocused() {
    return elements.draftIdeaExampleButtons.some((button) => button === document.activeElement);
  }

  function revokeDownloadUrl() {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
      downloadUrl = null;
    }
    elements.draftDownloadLink.classList.remove('is-ready');
  }
}

const draftSteps = ['idea', 'customize', 'review'] as const;
type DraftStep = (typeof draftSteps)[number];
type ProgressStepKind = Exclude<DraftStep, 'review'> | 'choice';

function setFormDisabled(form: HTMLFormElement, disabled: boolean) {
  form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select, button').forEach((element) => {
    element.disabled = disabled;
  });
}

function setProgressInputsDisabled(disabled: boolean) {
  document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('#draftProgress input, #draftProgress textarea, #draftProgress select').forEach((element) => {
    element.disabled = disabled;
  });
}

function isEmptyAnswer(answer: string | string[] | undefined) {
  return answer === undefined || (Array.isArray(answer) ? answer.length === 0 : answer.length === 0);
}
